import * as React from "react";
import { Chart, useChart } from "@chakra-ui/charts";
import { Box, Card, HStack, SimpleGrid, Stack, Tabs, Text } from "@chakra-ui/react";
import { Bar, BarChart, CartesianGrid, LabelList, Tooltip, XAxis, YAxis } from "recharts";
import AppAvatar from "components/utils/AppAvatar";

const CHART_MODES = {
  histograma: {
    label: "Histograma",
    title: "Histograma de asistencia",
    subtitle: "Distribucion de instructores por asistencias",
  },
  puntualidad: {
    label: "Puntualidad",
    title: "Puntualidad",
    subtitle: "Resumen general de asistencia del periodo",
  },
};

const PUNCTUALITY_SERIES = [
  { key: "aTiempoCount", label: "A tiempo", color: "green.solid" },
  { key: "tardeCount", label: "Tarde", color: "orange.solid" },
  { key: "tempranoCount", label: "Temprano", color: "cyan.solid" },
  { key: "tardeTempranoCount", label: "Tarde+Temprano", color: "red.solid" },
  { key: "remplazosCount", label: "Reemplazos", color: "purple.solid" },
];

function getAttendanceHistogram(data) {
  const counts = new Map();

  for (const item of data) {
    const asistencias = Number(item.asistenciasCount ?? 0);
    const current = counts.get(asistencias) ?? [];
    current.push({
      id: item.id,
      nombre: item.nombre || "Instructor",
      avatarSrc: item.avatarSrc,
      avatarColor: item.avatarColor,
    });
    counts.set(asistencias, current);
  }

  return Array.from(counts.entries())
    .map(([asistencias, instructores]) => ({
      asistencias,
      label: String(asistencias),
      instructores: instructores.length,
      instructorItems: instructores.sort((a, b) => a.nombre.localeCompare(b.nombre)),
    }))
    .sort((a, b) => a.asistencias - b.asistencias);
}

function HistogramTooltip({ active, payload }) {
  const bucket = payload?.[0]?.payload;
  if (!active || !bucket) return null;

  return (
    <Box
      bg="dashboard.bg.surface"
      borderWidth="1px"
      borderColor="dashboard.border.default"
      rounded="xl"
      boxShadow="xl"
      px="3"
      py="3"
      minW="230px"
      maxW="300px"
    >
      <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase">
        {bucket.asistencias} asistencias
      </Text>
      <Stack gap="2" mt="2">
        {(bucket.instructorItems || []).map((item) => (
          <HStack key={item.id || item.nombre} gap="2.5" minW="0">
            <AppAvatar name={item.nombre} src={item.avatarSrc} color={item.avatarColor} size={28} />
            <Text fontSize="sm" color="dashboard.fg.default" fontWeight="700" lineHeight="1.2" truncate>
              {item.nombre}
            </Text>
          </HStack>
        ))}
      </Stack>
    </Box>
  );
}

function EmptyState({ children }) {
  return (
    <Box
      minH="260px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      rounded="card"
      bg="dashboard.bg.surfaceMuted"
    >
      <Text color="dashboard.fg.muted" fontSize="sm">
        {children}
      </Text>
    </Box>
  );
}

function AttendanceHistogram({ data }) {
  const chartData = React.useMemo(() => getAttendanceHistogram(data), [data]);
  const total = React.useMemo(
    () => chartData.reduce((sum, item) => sum + Number(item.instructores ?? 0), 0),
    [chartData],
  );
  const chart = useChart({
    data: chartData,
    series: [{ name: "instructores", color: "arawaks.tropicalTeal" }],
  });
  const series = chart.getSeries({ name: "instructores" });

  if (chartData.length === 0 || total === 0) {
    return <EmptyState>Sin asistencias registradas</EmptyState>;
  }

  return (
    <Chart.Root height="260px" chart={chart}>
      <BarChart data={chart.data} margin={{ top: 12, right: 16, bottom: 8, left: 0 }}>
        <defs>
          <linearGradient id="attendanceHistogramFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={chart.color(series?.color)} stopOpacity={0.96} />
            <stop offset="100%" stopColor={chart.color(series?.color)} stopOpacity={0.62} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--chakra-colors-dashboard-border-muted)" />
        <XAxis
          dataKey={chart.key("label")}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontWeight: 700, fill: "var(--chakra-colors-dashboard-fg-muted)" }}
        />
        <YAxis
          allowDecimals={false}
          axisLine={false}
          tickLine={false}
          width={36}
          tick={{ fontSize: 12, fontWeight: 700, fill: "var(--chakra-colors-dashboard-fg-muted)" }}
        />
        <Tooltip cursor={{ fill: "var(--chakra-colors-dashboard-bg-surface-muted)" }} content={<HistogramTooltip />} />
        <Bar dataKey={chart.key("instructores")} fill="url(#attendanceHistogramFill)" radius={[10, 10, 4, 4]} maxBarSize={52}>
          <LabelList
            dataKey={chart.key("instructores")}
            position="top"
            fontSize={12}
            fontWeight={800}
            fill="var(--chakra-colors-dashboard-fg-default)"
          />
        </Bar>
      </BarChart>
    </Chart.Root>
  );
}

function PunctualitySegmentedBar({ data }) {
  const [activeSegment, setActiveSegment] = React.useState(null);
  const segments = React.useMemo(
    () =>
      PUNCTUALITY_SERIES.map((item) => ({
        ...item,
        value: data.reduce((sum, row) => sum + Number(row[item.key] ?? 0), 0),
      })),
    [data],
  );
  const total = segments.reduce((sum, segment) => sum + Number(segment.value || 0), 0);
  const active = activeSegment || null;

  if (total <= 0) {
    return <EmptyState>Sin registros de puntualidad</EmptyState>;
  }

  return (
    <Stack gap="4" minH="260px" justify="center">
      <HStack justify="space-between" align="end" gap="4">
        <Box minW="0">
          <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase">
            {active ? active.label : "Total de registros"}
          </Text>
          <Text fontFamily="display" fontSize="3xl" color="dashboard.fg.default" fontWeight="700" lineHeight="1" mt="1">
            {active ? active.value : total}
          </Text>
        </Box>
        <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textAlign="right" maxW="44">
          {active ? `${Math.round((Number(active.value || 0) / total) * 100)}% del total` : `${total} registros`}
        </Text>
      </HStack>

      <HStack gap="1" h="16" rounded="2xl" overflow="hidden" bg="dashboard.bg.surfaceMuted" p="1">
        {segments.map((segment) => {
          const width = `${Math.max(0, (Number(segment.value || 0) / total) * 100)}%`;
          const isActive = active?.key === segment.key;
          return (
            <Box
              key={segment.key}
              h="full"
              minW={segment.value > 0 ? "8px" : "0"}
              flex={`0 0 ${width}`}
              bg={segment.color}
              opacity={isActive || !active ? 1 : 0.42}
              rounded="xl"
              cursor={segment.value > 0 ? "pointer" : "default"}
              transition="opacity 140ms ease, transform 140ms ease"
              transform={isActive ? "translateY(-2px)" : "none"}
              onMouseEnter={() => segment.value > 0 && setActiveSegment(segment)}
              onMouseLeave={() => setActiveSegment(null)}
              onFocus={() => segment.value > 0 && setActiveSegment(segment)}
              onBlur={() => setActiveSegment(null)}
              tabIndex={segment.value > 0 ? 0 : undefined}
            />
          );
        })}
      </HStack>

      <SimpleGrid columns={{ base: 1, sm: 2 }} gap="3">
        {segments.map((segment) => {
          const isActive = active?.key === segment.key;
          return (
            <HStack
              key={segment.key}
              gap="2.5"
              minW="0"
              cursor={segment.value > 0 ? "pointer" : "default"}
              rounded="lg"
              px="2"
              py="1.5"
              bg={isActive ? "dashboard.bg.surfaceMuted" : "transparent"}
              onMouseEnter={() => segment.value > 0 && setActiveSegment(segment)}
              onMouseLeave={() => setActiveSegment(null)}
              onFocus={() => segment.value > 0 && setActiveSegment(segment)}
              onBlur={() => setActiveSegment(null)}
              tabIndex={segment.value > 0 ? 0 : undefined}
            >
              <Box boxSize="2.5" rounded="full" bg={segment.color} flexShrink={0} />
              <Box minW="0">
                <Text fontSize="sm" color="dashboard.fg.default" fontWeight="800" lineClamp={1}>
                  {segment.label}
                </Text>
                <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="700">
                  {segment.value} - {Math.round((Number(segment.value || 0) / total) * 100)}%
                </Text>
              </Box>
            </HStack>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}

function InstructorPerformanceCharts({ data = [] }) {
  const [mode, setMode] = React.useState("histograma");
  const currentMode = CHART_MODES[mode] ? mode : "histograma";

  return (
    <Card.Root bg="dashboard.bg.surface" borderWidth="1px" borderColor="dashboard.border.muted" rounded="card">
      <Tabs.Root
        value={currentMode}
        onValueChange={(e) => {
          if (e.value) setMode(e.value);
        }}
      >
        <Card.Header gap="4" alignItems="stretch">
          <HStack justify="space-between" align="flex-start" gap="4" wrap="wrap">
            <Box>
              <Text fontFamily="display" fontWeight="700" fontSize="lg" color="dashboard.fg.default">
                {CHART_MODES[currentMode].title}
              </Text>
              <Text fontSize="sm" color="dashboard.fg.muted">
                {CHART_MODES[currentMode].subtitle}
              </Text>
            </Box>

            <Tabs.List bg="dashboard.bg.surfaceMuted" p="1" rounded="lg">
              <Tabs.Trigger value="histograma" rounded="md" px="3">
                {CHART_MODES.histograma.label}
              </Tabs.Trigger>
              <Tabs.Trigger value="puntualidad" rounded="md" px="3">
                {CHART_MODES.puntualidad.label}
              </Tabs.Trigger>
              <Tabs.Indicator rounded="md" />
            </Tabs.List>
          </HStack>
        </Card.Header>

        <Card.Body pt="8">
          <Tabs.Content value="histograma" p="0">
            <AttendanceHistogram data={data} />
          </Tabs.Content>
          <Tabs.Content value="puntualidad" p="0">
            <PunctualitySegmentedBar data={data} />
          </Tabs.Content>
        </Card.Body>
      </Tabs.Root>
    </Card.Root>
  );
}

export default InstructorPerformanceCharts;
